import { useEffect, useState } from "react";
import { site, treatments } from "@/mocks/site-data";
import Reveal from "@/components/feature/Reveal";

export default function Treatments() {
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (!selected) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSelected(null);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [selected]);

  return (
    <section id="tratamentos" className="scroll-mt-28 bg-background-50 py-20 md:py-28">
      <div className="mx-auto max-w-8xl px-4 md:px-6">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-400">
            Tratamentos e procedimentos
          </p>
          <h2 className="mt-3 max-w-2xl font-heading text-3xl font-medium leading-tight text-foreground-50 md:text-5xl">
            {treatments.title}
          </h2>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-foreground-400">
            {treatments.intro}
          </p>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
          {treatments.categories.map((cat, i) => (
            <Reveal key={cat.title} delay={i * 80}>
              <div className="flex h-full flex-col rounded-lg border border-secondary-300 bg-background-50 p-6 md:p-8">
                <h3 className="font-heading text-2xl font-semibold text-foreground-50">
                  {cat.title}
                </h3>
                <ul className="mt-5 flex flex-col divide-y divide-secondary-300">
                  {cat.items.map((item) => (
                    <li key={item}>
                      <button
                        type="button"
                        onClick={() => setSelected(item)}
                        className="group flex w-full items-center justify-between gap-3 py-3 text-left"
                      >
                        <span className="text-sm font-medium text-foreground-300 transition-colors group-hover:text-accent-400 md:text-base">
                          {item}
                        </span>
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-secondary-400 text-foreground-500 transition-colors group-hover:border-accent-500 group-hover:text-accent-400">
                          <i className="ri-add-line text-base" />
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <p className="mt-8 max-w-3xl text-sm leading-relaxed text-foreground-500">
            {treatments.disclaimer}
          </p>
        </Reveal>
      </div>

      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-background-50/70 p-0 backdrop-blur-sm sm:items-center sm:p-4"
          onClick={() => setSelected(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="treatment-modal-title"
            className="w-full max-w-lg rounded-t-2xl bg-background-100 p-6 sm:rounded-2xl sm:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <h3
                id="treatment-modal-title"
                className="font-heading text-2xl font-semibold text-foreground-50"
              >
                {selected}
              </h3>
              <button
                type="button"
                onClick={() => setSelected(null)}
                aria-label="Fechar"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-secondary-400 text-foreground-500 transition-colors hover:text-foreground-50"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <p className="mt-4 text-sm leading-relaxed text-foreground-400 md:text-base">
              Para saber se {selected} pode ser adequado para o seu caso, é necessária uma avaliação
              médica individual. Benefícios, riscos, contraindicações e alternativas serão
              discutidos durante a consulta.
            </p>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <a
                href={site.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-400"
              >
                <i className="ri-whatsapp-line text-base" />
                Quero saber mais
              </a>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="inline-flex flex-1 items-center justify-center whitespace-nowrap rounded-full border border-secondary-400 px-6 py-3 text-sm font-semibold text-foreground-400 transition-colors hover:border-accent-500 hover:text-accent-400"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}